import { useMsal } from "@azure/msal-react";
import {
    Button,
    Checkbox,
    Divider,
    Flex,
    Group,
    Loader,
    Modal,
    Stack,
    Switch,
    Text,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { useFeature } from "flagged";
import { cloneElement, memo, type ReactElement, useState } from "react";
import { useUserDatasources } from "src/hooks/useUser";
import { deleteAPI, postAPI } from "src/utils/fetch";
import { db } from "../db";
import { DeleteChatsModal } from "./DeleteChatsModal";

// Check if running in copilot mode
const isCopilotMode =
    typeof window !== "undefined" &&
    (window.location.hash.includes("/copilot") || window.location.search.includes("mode=copilot"));

interface SettingsModalProps {
    children?: ReactElement;
    opened?: boolean;
    onClose?: () => void;
}

export const SettingsModal = memo(function SettingsModal({
    children,
    opened: controlledOpened,
    onClose: controlledOnClose,
}: SettingsModalProps) {
    const is_memory_flag_enabled = useFeature("ENABLE_MEMORY_FLAG");
    const { instance } = useMsal();
    const [internalOpened, { open, close: internalClose }] = useDisclosure(false);

    // Support both controlled and uncontrolled modes
    const isControlled = controlledOpened !== undefined;
    const opened = isControlled ? controlledOpened : internalOpened;
    const close = isControlled ? (controlledOnClose ?? (() => {})) : internalClose;
    const chatsCount = useLiveQuery(async () => {
        return (await db.chats.toArray()).length;
    }, []);
    const messagesCount = useLiveQuery(async () => {
        return (await db.messages.toArray()).length;
    }, []);
    const [memoryEnabled, setMemoryEnabled] = useState(() => {
        const stored = localStorage.getItem("memoryEnabled");
        return stored === "true";
    });
    const queryClient = useQueryClient();
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

    // Data source selection for copilot mode
    const { data: userDatasourceDetails, isLoading: isLoadingUser } = useUserDatasources();
    const [chatPreferences, setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>({
        key: "chatPreferences",
        defaultValue: { selectedProductLines: [], selectedDatasources: [] },
    });

    // Get available datasources from user data
    const availableDatasources = userDatasourceDetails?.filter((ds) => ds.has_access) || [];

    // Handle datasource toggle
    const handleDatasourceToggle = (datasourceId: string, checked: boolean) => {
        setChatPreferences((prev) => {
            const newSelectedDatasources = checked
                ? [...prev.selectedDatasources, datasourceId]
                : prev.selectedDatasources.filter((id) => id !== datasourceId);
            return { ...prev, selectedDatasources: newSelectedDatasources };
        });
    };
    const [isClearingMemory, setIsClearingMemory] = useState(false);
    const handleClearMemory = async () => {
        setIsClearingMemory(true);
        try {
            await deleteAPI({
                url: `${BACKEND_URL}/v1/user/memory`,
                instance,
            });
        } catch (error) {
            console.error("Error clearing memory: ", error);
        } finally {
            setIsClearingMemory(false);
        }
    };

    const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.checked;
        setMemoryEnabled(value);
        localStorage.setItem("memoryEnabled", value.toString());
        const storedPreferences = localStorage.getItem("chatPreferences");
        const chatPreferences = storedPreferences
            ? JSON.parse(storedPreferences)
            : {
                  selectedProductLines: [],
                  selectedDatasources: [],
              };
        const product_line =
            chatPreferences.selectedProductLines.length > 1
                ? chatPreferences.selectedProductLines[0]
                : "";
        try {
            await postAPI({
                url: `${BACKEND_URL}/v1/user/preferences`,
                payload: {
                    memory_enabled: value,
                    product_line: product_line,
                    datasources: chatPreferences.selectedDatasources,
                },
                instance,
            });
            await queryClient.invalidateQueries({ queryKey: ["user"] });
        } catch (error) {
            console.error("Error updating user preferences: ", error);
        }
    };
    return (
        <>
            {children &&
                cloneElement(children, { onClick: open } as React.HTMLAttributes<HTMLElement>)}
            <Modal
                opened={opened}
                onClose={close}
                title="Settings"
                size="lg"
                withinPortal
                keepMounted
            >
                <Stack>
                    {/* Data Sources Section - Show in copilot mode */}
                    {isCopilotMode && (
                        <>
                            <Text size="md" fw={700}>
                                Data Sources
                            </Text>
                            <Text size="xs" c="dimmed" mb="xs">
                                Select which data sources to search when asking questions
                            </Text>
                            {isLoadingUser ? (
                                <Flex justify="center" py="md">
                                    <Loader size="sm" />
                                </Flex>
                            ) : availableDatasources.length > 0 ? (
                                <Stack gap="xs">
                                    {availableDatasources.map((ds) => (
                                        <Checkbox
                                            key={ds.id}
                                            label={ds.name}
                                            checked={chatPreferences.selectedDatasources.includes(
                                                ds.id,
                                            )}
                                            onChange={(event) =>
                                                handleDatasourceToggle(
                                                    ds.id,
                                                    event.currentTarget.checked,
                                                )
                                            }
                                            styles={{
                                                body: { alignItems: "flex-start" },
                                                description: { marginTop: 2 },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Text size="sm" c="dimmed" fs="italic">
                                    No data sources available. Contact your administrator for
                                    access.
                                </Text>
                            )}
                            <Divider my="md" />
                        </>
                    )}

                    {is_memory_flag_enabled ? (
                        <>
                            <Text size="md" fw={700}>
                                Memory
                            </Text>
                            <Group align="center" mb="md">
                                <Switch
                                    label="Enable memory"
                                    checked={memoryEnabled}
                                    onChange={handleToggle}
                                />
                                <Button
                                    variant="outline"
                                    color="red"
                                    leftSection={<IconTrash size={20} />}
                                    loading={isClearingMemory}
                                    onClick={handleClearMemory}
                                >
                                    Clear
                                </Button>
                            </Group>
                            <Divider my="md" />
                        </>
                    ) : null}

                    {/* Database Section */}
                    <Text size="md" fw={700}>
                        User's History
                    </Text>
                    <Group gap="xl" align="center">
                        <Group gap={6} align="baseline">
                            <Text size="xl" fw={700} lh={1}>
                                {chatsCount ?? 0}
                            </Text>
                            <Text size="sm" c="dimmed">
                                conversations
                            </Text>
                        </Group>
                        <Text c="gray.3">·</Text>
                        <Group gap={6} align="baseline">
                            <Text size="xl" fw={700} lh={1}>
                                {messagesCount ?? 0}
                            </Text>
                            <Text size="sm" c="dimmed">
                                questions & answers
                            </Text>
                        </Group>
                        <DeleteChatsModal onOpen={close} />
                    </Group>
                </Stack>
            </Modal>
        </>
    );
});
